'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { getCountry, getAllCountries, getCountryFlag } from '@/lib/GeoGraph';
import { MapPin, X, ArrowRight, Check } from 'lucide-react';

interface MarketMismatchBannerProps {
  currentCountrySlug: string;
}

export default function MarketMismatchBanner({ currentCountrySlug }: MarketMismatchBannerProps) {
  const [detectedSlug, setDetectedSlug] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(true);
  const pathname = usePathname();

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      if (sessionStorage.getItem('p4d_dismiss_mismatch')) return;

      // Check cookie first
      const match = document.cookie.match(/p4d_market_country=([a-zA-Z]{2})/i);
      let detectedCode: string | null = match ? match[1].toLowerCase() : null;

      // If not in cookie, check if edge injected meta or header
      if (!detectedCode) {
        const metaCountry = document.querySelector('meta[name="x-market-country"]')?.getAttribute('content');
        if (metaCountry && metaCountry.length === 2) {
          detectedCode = metaCountry.toLowerCase();
        }
      }

      const supported = ['ng', 'gh', 'ke', 'za', 'ci', 'sn', 'tg', 'bj'];
      if (detectedCode && supported.includes(detectedCode) && detectedCode !== currentCountrySlug.toLowerCase()) {
        setDetectedSlug(detectedCode);
        setIsDismissed(false);
      }
    } catch {
      // Fail safely
    }
  }, [currentCountrySlug]);

  if (isDismissed || !detectedSlug) return null;

  const currentCountry = getCountry(currentCountrySlug);
  const targetCountry = getCountry(detectedSlug);

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      sessionStorage.setItem('p4d_dismiss_mismatch', 'true');
    } catch {}
  };

  const handleSwitch = () => {
    try {
      document.cookie = `p4d_market_country=${targetCountry.code}; path=/; max-age=31536000; SameSite=Lax`;
    } catch {}
    // Calculate new target path
    const segments = (pathname || '').split('/').filter(Boolean);
    if (segments[0] === currentCountrySlug) {
      segments[0] = targetCountry.slug;
      window.location.href = '/' + segments.join('/');
    } else {
      window.location.href = `/${targetCountry.slug}`;
    }
  };

  return (
    <aside
      aria-label="Market mismatch notification"
      className="bg-[#0A1B4A] text-white border-b border-blue-900/50 px-4 py-2.5 text-xs transition-all relative z-50 shadow-xs"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="p-1 rounded-md bg-blue-500/20 text-blue-300">
            <MapPin className="size-3.5" />
          </span>
          <p className="text-slate-200">
            You are browsing inventory in{' '}
            <strong className="text-white font-bold">{currentCountry.name}</strong> ({currentCountry.currency}).
            Looking for local delivery in{' '}
            <strong className="text-emerald-400 font-bold">{targetCountry.name}</strong>?
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSwitch}
            className="px-3 py-1 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold inline-flex items-center gap-1.5 transition-all cursor-pointer shadow-xs active:scale-95"
          >
            <span>{targetCountry.flag || getCountryFlag(targetCountry.code)}</span>
            <span>Switch to {targetCountry.name}</span>
            <ArrowRight className="size-3" />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title="Dismiss notice"
            aria-label="Dismiss notice"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
